import { apiGetShareeDrivesUUIDs } from '@/api/functions'
import { db } from '@/db'
import { ensureCurrentUser } from '@/services/auth'
import {
  driveIsPreparing,
  driveIsReady,
  getActiveResources,
  getExistingResources,
} from '@/services/selectors'
import { sortAlphabeticallyByAProperty } from '@/services/sorting'

import { DriveDocument } from '@/api/types/drive'
import { ActionTree, Commit, Dispatch, GetterTree, Module, MutationTree } from 'vuex'
import { firestoreAction } from 'vuexfire'
import { DriveRequestName, DriveRequests, DriveRequestUpdate, DriveState } from '../types/drive'
import { RequestInfo, RequestStatus, RootState } from '../types/root'
import { apiUpdateDrive } from '@/api/drive'

const getDefaultState = (): DriveState => {
  return {
    ownerDrives: null,
    shareeDrives: null,
    selectedDriveUuid: null,
    drivesRequests: {},
  }
}

const moduleState = getDefaultState()

export const updateDriveRequest = (
  commit: Commit,
  dispatch: Dispatch,
  uuid: string,
  name: DriveRequestName,
  status: RequestStatus,
  notificationContent?: string,
) => {
  if (!moduleState.drivesRequests || !moduleState.drivesRequests[uuid]) {
    commit('CREATE_DRIVE_REQUEST_MAP', uuid)
  }
  const error = status === 'failed' ? notificationContent : undefined
  commit('SET_DRIVE_REQUEST_INFO', new DriveRequestUpdate(uuid, name, status, error))
  if (notificationContent) {
    if (status === 'successful' || status === 'pending') {
      dispatch('notification/success', notificationContent, { root: true })
    } else if (status === 'failed') {
      dispatch('notification/error', notificationContent, { root: true })
    }
  }
}

export const initDriveRequestHelper = (
  commit: Commit,
  dispatch: Dispatch,
  uuid: string,
  name: DriveRequestName,
  optionalPendingNotificationText?: string,
) => {
  updateDriveRequest(commit, dispatch, uuid, name, 'pending', optionalPendingNotificationText)
  const setSuccess = (notificationText?: string) =>
    updateDriveRequest(commit, dispatch, uuid, name, 'successful', notificationText)
  const setFailure = (error?: string) =>
    updateDriveRequest(commit, dispatch, uuid, name, 'failed', error)
  return [setSuccess, setFailure]
}

const moduleGetters: GetterTree<DriveState, RootState> = {
  drives(state) {
    if (!state.ownerDrives || !state.shareeDrives) {
      // null is used as a sentinel value for the loading state.
      return null
    }
    let drivesToShow: DriveDocument[] = []
    if (state.ownerDrives !== null && state.ownerDrives.length > 0) {
      drivesToShow = drivesToShow.concat(state.ownerDrives)
    }
    if (state.shareeDrives !== null && state.shareeDrives.length > 0) {
      drivesToShow = drivesToShow.concat(state.shareeDrives)
    }
    sortAlphabeticallyByAProperty(drivesToShow)
    return getActiveResources(drivesToShow)
  },
  info:
    (state, getters) =>
    (uuid: string): DriveDocument | {} => {
      if (!state.ownerDrives && !state.shareeDrives) {
        return {}
      }
      const found = getters.drives.find((drive: DriveDocument) => drive.uuid === uuid)

      if (found) {
        return found
      }
      return {}
    },
  canAssign:
    (_state, getters, _rootState, rootGetters) =>
    (uuid: string): boolean => {
      const { uid } = ensureCurrentUser()
      const driveInfo = getters.info(uuid)
      const isDriveOwner = uid === driveInfo.owner_id
      return rootGetters['user/hasTeamsEnabled'] && isDriveOwner
    },
  errored:
    (_state, getters) =>
    (uuid: string): boolean => {
      const d = getters.info(uuid)
      return d.status === 'error'
    },
  displayError:
    (_state, getters) =>
    (uuid: string): string => {
      const d = getters.info(uuid)
      return d.display_error
    },
  statusInProgress:
    (_state, getters) =>
    (uuid: string): boolean => {
      const d = getters.info(uuid)
      // kind to determine if drive does have newer format (not the legacy one)
      return driveIsPreparing(d)
    },
  displayStatus:
    (_state, getters) =>
    (uuid: string): string => {
      const d = getters.info(uuid)
      if (driveIsReady(d)) {
        return ''
      }
      return d.status
    },
  selected(state, getters) {
    const info = getters.info(state.selectedDriveUuid)
    if (!info.uuid) {
      return null
    }
    return info
  },
  selectedDriveUuid(state) {
    return state.selectedDriveUuid
  },
  createDriveRequest(_state, _getters, rootState) {
    return rootState.requests.createDrive
  },
  ownsAny(state, _getters): boolean {
    const isLoaded = Array.isArray(state.ownerDrives)
    if (!isLoaded) return false
    return state.ownerDrives!.some((drive) => !drive.deleted)
  },
  isBeingCreated(_state, getters): boolean {
    return Array.isArray(getters.drives) && getters.drives.some(driveIsPreparing)
  },
  numberOfOwnerDrives(state): number {
    const { ownerDrives } = state
    return getExistingResources(ownerDrives).length
  },
  requestInfo:
    (state) =>
    (uuid: string): DriveRequests | undefined => {
      if (state.drivesRequests && state.drivesRequests[uuid]) {
        return state.drivesRequests[uuid]
      }
      return undefined
    },
}

const moduleActions: ActionTree<DriveState, RootState> = {
  async bindUserDrives({ dispatch, getters }) {
    /**
     *  Why are we having these two alongside?
     *
     * The most important thing here is real-time binding with firestore.
     * Connector endpoint returns uuids of machines shared with our user.
     * To get uuids of machines owned, we would have to query the firestore.
     * We are getting the two together in the drive getter.
     */

    return Promise.all([dispatch('bindOwnerDrives'), dispatch('bindShareeDrives')])
  },

  bindOwnerDrives: firestoreAction(({ bindFirestoreRef, state }) => {
    // avoid rebinding when unnecessary
    if (state.ownerDrives !== null) return
    const currentUser = ensureCurrentUser()
    return bindFirestoreRef(
      'ownerDrives',
      db.collection('drives').where('owner_id', '==', currentUser.uid),
      { wait: true },
    )
  }),

  bindShareeDrives: firestoreAction(async ({ bindFirestoreRef, commit }) => {
    // not avoiding rebounding - with every call we want binding with new uuidList
    const uuidAndUsersInfoList = await apiGetShareeDrivesUUIDs()
    const uuidList = uuidAndUsersInfoList.map((obj: any) => obj.uuid)

    // the vuexfire library disallows binding with 'in' opertator on empty array,
    // so we manually set shareeDrives to empty array if no shared drives are returned from the API.
    if (uuidList.length) {
      return bindFirestoreRef(
        'shareeDrives',
        db.collection('drives').where('uuid', 'in', uuidList), // it must be passed unempty
        { wait: true },
      )
    }
    return commit('SET_SHAREE_DRIVES', [])
  }),

  async resetDriveState({ commit }) {
    commit('resetState')
  },
  async updateDrive({ commit, dispatch, getters, rootGetters }, { uuid, name, customDriveLetter }) {
    const [setSuccess, setFailure] = initDriveRequestHelper(commit, dispatch, uuid, 'updateDrive')
    try {
      await apiUpdateDrive(uuid, name, customDriveLetter)
      setSuccess('Drive updated')
    } catch (err: any) {
      setFailure(err)
      throw err
    }
  },
}

const moduleMutations: MutationTree<DriveState> = {
  SET_DRIVE_REQUEST_INFO(
    state,
    { uuid, name, info }: { uuid: string; name: DriveRequestName; info: RequestInfo },
  ) {
    state.drivesRequests[uuid][name] = info
  },
  CREATE_DRIVE_REQUEST_MAP(state, uuid) {
    state.drivesRequests = {
      ...state.drivesRequests,
      [uuid]: {
        updateDrive: new RequestInfo(),
      },
    }
  },
  select(state, { uuid }) {
    state.selectedDriveUuid = uuid
  },
  deselect(state) {
    state.selectedDriveUuid = null
  },
  SET_SHAREE_DRIVES(state, newShareeDrives) {
    state.shareeDrives = newShareeDrives
  },
  resetState(state) {
    Object.assign(state, getDefaultState())
  },
}

const drive: Module<DriveState, RootState> = {
  namespaced: true,
  // prefixed with "module-", so that they do not get shadowed by desctructured context
  state: moduleState,
  getters: moduleGetters,
  actions: moduleActions,
  mutations: moduleMutations,
}
export default drive
